import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { IoIosMenu, IoIosClose } from "react-icons/io";
import {
  FaFacebookF,
  FaInstagram,
  FaSun,
  FaMoon,
  FaCog,
  FaChevronDown,
  FaUsers,
} from "react-icons/fa";
import {
  FiSearch,
  FiPhone,
  FiMail,
  FiMapPin,
  FiGlobe,
  FiExternalLink,
} from "react-icons/fi";

import { getSettings } from "./../../../services/settingsServices";
import { getAllCategoriesAsso } from "./../../../services/categoriesAssoServices";
import { getAllSections } from "./../../../services/sectionsServices";

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

const LIGHT_THEME = "monappliasso";
const DARK_THEME = "monappliasso_dark";

const cn = (...classes) => classes.filter(Boolean).join(" ");

const NAV = [
  { label: "Accueil", to: "/" },
  { label: "Infrastructures", to: "/infrastructures" },
  { label: "Carte", to: "/carte" },
  { label: "Contact", to: "/contact" },
];

function getInitialTheme() {
  const stored = localStorage.getItem("theme");

  if (stored === "dark" || stored === "light") return stored;

  const html = document.documentElement;

  if (
    html.classList.contains("dark") ||
    String(html.getAttribute("data-theme") || "").includes("dark")
  ) {
    return "dark";
  }

  return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches
    ? "dark"
    : "light";
}

function applyTheme(theme) {
  const isDark = theme === "dark";
  const html = document.documentElement;

  html.classList.toggle("dark", isDark);
  html.setAttribute("data-theme", isDark ? DARK_THEME : LIGHT_THEME);

  localStorage.setItem("theme", isDark ? "dark" : "light");
}

function normalizeText(value = "") {
  return String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function toMediaUrl(url = "") {
  const value = String(url || "").trim();

  if (!value) return "";

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (value.startsWith("/uploads")) {
    return `${API_BASE}${value}`;
  }

  return value;
}

function sectionUrl(section) {
  const path = section?.lien_section;

  if (typeof path === "string" && path.trim().startsWith("/")) {
    return path.trim();
  }

  if (section?.slug) {
    const slug = String(section.slug).trim();
    return slug.startsWith("/") ? slug : `/sections/${slug}`;
  }

  if (section?.id) return `/sections/${section.id}`;

  return "/sections";
}

function niceUrl(url = "") {
  if (!url) return "";

  return String(url)
    .replace(/^https?:\/\//, "")
    .replace(/\/$/, "");
}

function isExternalUrl(url = "") {
  return /^https?:\/\//i.test(String(url || ""));
}

function openExternal(url) {
  if (!url) return;

  const target = isExternalUrl(url) ? url : `https://${url}`;
  window.open(target, "_blank", "noopener,noreferrer");
}

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileAssociationsOpen, setMobileAssociationsOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(getInitialTheme() === "dark");

  const [clubInfo, setClubInfo] = useState({
    logo: "",
    nom: "MonAppliAsso",
    facebook: "",
    instagram: "",
  });

  const [categories, setCategories] = useState([]);
  const [activeCatId, setActiveCatId] = useState(0);
  const [allSections, setAllSections] = useState([]);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [q, setQ] = useState("");

  const headerRef = useRef(null);
  const megaRef = useRef(null);

  const associationsActive =
    location.pathname.startsWith("/sections") ||
    location.pathname.startsWith("/associations");

  const go = (to) => {
    navigate(to);
    setMobileOpen(false);
    setMobileAssociationsOpen(false);
    setMegaOpen(false);
  };

  useEffect(() => {
    setMobileOpen(false);
    setMobileAssociationsOpen(false);
    setMegaOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    applyTheme(darkMode ? "dark" : "light");
  }, [darkMode]);

  useEffect(() => {
    if (!mobileOpen) {
      document.body.style.overflow = "";
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setMobileOpen(false);
        setMobileAssociationsOpen(false);
        setMegaOpen(false);
      }
    };

    const onClick = (event) => {
      if (!megaOpen) return;

      const megaEl = megaRef.current;
      const headerEl = headerRef.current;

      if (!megaEl || !headerEl) return;

      if (megaEl.contains(event.target) || headerEl.contains(event.target)) {
        return;
      }

      setMegaOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onClick);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onClick);
    };
  }, [megaOpen]);

  useEffect(() => {
    let mounted = true;

    async function fetchSettings() {
      try {
        const settings = await getSettings();
        const s = Array.isArray(settings) ? settings[0] : settings;

        if (!mounted || !s) return;

        setClubInfo({
          logo: s.logo_asso || s.logo || "",
          nom: s.nom_asso || s.nom || "MonAppliAsso",
          facebook: s.facebook_asso || s.facebook || "",
          instagram: s.instagram_asso || s.instagram || "",
        });
      } catch (error) {
        console.error("Erreur settings:", error);
      }
    }

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function fetchData() {
      try {
        const [cats, secs] = await Promise.all([
          getAllCategoriesAsso({
            with_counts: 1,
            is_active: true,
          }),
          getAllSections(),
        ]);

        if (!mounted) return;

        const cleanCats = (Array.isArray(cats) ? cats : [])
          .filter((category) => category?.is_active !== false)
          .sort((a, b) => {
            const orderA = Number(a.ordre ?? 999);
            const orderB = Number(b.ordre ?? 999);

            if (orderA !== orderB) return orderA - orderB;

            return String(a.label || "").localeCompare(
              String(b.label || ""),
              "fr",
              { sensitivity: "base" },
            );
          });

        setCategories([
          { id: 0, label: "Toutes", slug: "toutes" },
          ...cleanCats,
        ]);

        setAllSections(Array.isArray(secs) ? secs : []);
        setActiveCatId(0);
      } catch (error) {
        console.error("Erreur chargement navbar:", error);

        if (!mounted) return;

        setCategories([{ id: 0, label: "Toutes", slug: "toutes" }]);
        setAllSections([]);
        setActiveCatId(0);
      }
    }

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  const activeSections = useMemo(() => {
    return (allSections || [])
      .filter((section) => section?.is_active !== false)
      .sort((a, b) => {
        const orderA = Number(a.ordre ?? 999);
        const orderB = Number(b.ordre ?? 999);

        if (orderA !== orderB) return orderA - orderB;

        return String(a.nom_section || "").localeCompare(
          String(b.nom_section || ""),
          "fr",
          { sensitivity: "base" },
        );
      });
  }, [allSections]);

  const allCount = activeSections.length;

  const categoryCounts = useMemo(() => {
    return activeSections.reduce((acc, section) => {
      const key = String(section.category_id || "");

      if (!key) return acc;

      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [activeSections]);

  const filteredSections = useMemo(() => {
    const query = normalizeText(q.trim());
    const catId = Number(activeCatId);

    return activeSections
      .filter((section) => {
        if (catId === 0) return true;
        return Number(section.category_id) === catId;
      })
      .filter((section) => {
        if (!query) return true;

        const haystack = normalizeText(
          [
            section.nom_section,
            section.discipline_section,
            section.responsable_section,
            section.adresse_section,
          ]
            .filter(Boolean)
            .join(" "),
        );

        return haystack.includes(query);
      });
  }, [activeSections, activeCatId, q]);

  useEffect(() => {
    if (!megaOpen && !mobileAssociationsOpen) return;

    setSelectedSectionId((current) => {
      if (
        current &&
        filteredSections.some(
          (section) => String(section.id) === String(current),
        )
      ) {
        return current;
      }

      return filteredSections[0]?.id ?? null;
    });
  }, [filteredSections, megaOpen, mobileAssociationsOpen]);

  const selectedSection = useMemo(() => {
    return (
      filteredSections.find(
        (section) => String(section.id) === String(selectedSectionId),
      ) ||
      filteredSections[0] ||
      null
    );
  }, [filteredSections, selectedSectionId]);

  const activeCategory = useMemo(() => {
    return (
      categories.find(
        (category) => Number(category.id) === Number(activeCatId),
      ) || null
    );
  }, [categories, activeCatId]);

  const associationsTitle = useMemo(() => {
    if (!activeCategory || Number(activeCategory.id) === 0) {
      return "Toutes les associations";
    }

    const raw = String(activeCategory.label || "Catégorie").trim();
    const lower = raw.toLowerCase();

    return lower.includes("asso") ? raw : `Associations ${raw}`;
  }, [activeCategory]);

  const navLinkClass = ({ isActive }) =>
    cn(
      "relative px-1.5 py-2 text-[15px] font-bold transition-colors",
      "text-slate-700 hover:text-slate-950",
      "dark:text-slate-300 dark:hover:text-white",
      "after:absolute after:-bottom-0.5 after:left-0 after:h-[2px] after:w-full after:origin-left after:scale-x-0 after:bg-primary after:transition-transform after:duration-200 after:content-['']",
      isActive && "text-slate-950 after:scale-x-100 dark:text-white",
      !isActive && "hover:after:scale-x-100",
    );

  return (
    <header
      ref={headerRef}
      className={cn(
        "fixed left-0 right-0 top-0 z-[9990]",
        "border-b backdrop-blur-xl transition-colors duration-300",
        "border-slate-200 bg-slate-100/95 text-slate-900",
        "dark:border-white/10 dark:bg-slate-950/95 dark:text-white",
        scrolled && "shadow-lg shadow-slate-900/10 dark:shadow-black/40",
      )}
    >
      <div className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6">
        <div className="flex h-[70px] items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => go("/")}
            className="flex min-w-0 items-center gap-3"
            aria-label="Retour accueil"
          >
            <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden border p-1 shadow-sm dark:border-white/10 dark:bg-slate-900">
              {clubInfo.logo ? (
                <img
                  src={toMediaUrl(clubInfo.logo)}
                  alt={clubInfo.nom}
                  className="h-full w-full object-contain"
                />
              ) : (
                <div className="h-full w-full rounded-xl bg-gradient-to-br from-primary via-blue-600 to-emerald-500" />
              )}
            </div>

            <div className="min-w-0 text-left">
              <div className="truncate font-sans text-lg font-semibold leading-tight tracking-tight text-slate-950 dark:text-white sm:text-xl">
                {clubInfo.nom}
              </div>
              {/* <div className="truncate font-sans text-lg font-semibold leading-tight tracking-tight text-slate-950 dark:text-white sm:text-xl">
                MonAppli<span className="text-blue-500 font-extrabold">Asso</span>
              </div> */}

              <div className="hidden truncate text-xs font-medium text-slate-600 dark:text-slate-400 sm:block">
                Annuaire & vitrine des associations
              </div>
            </div>
          </button>

          <nav className="hidden items-center gap-6 lg:flex">
            <NavLink to={NAV[0].to} className={navLinkClass}>
              {NAV[0].label}
            </NavLink>

            <button
              type="button"
              onClick={() => setMegaOpen((value) => !value)}
              className={cn(
                "relative inline-flex items-center gap-2 px-1.5 py-2 text-[15px] font-bold transition-colors",
                "after:absolute after:-bottom-0.5 after:left-0 after:h-[2px] after:w-full after:origin-left after:bg-primary after:transition-transform after:duration-200 after:content-['']",
                megaOpen || associationsActive
                  ? "text-slate-950 after:scale-x-100 dark:text-white"
                  : "text-slate-700 after:scale-x-0 hover:text-slate-950 hover:after:scale-x-100 dark:text-slate-300 dark:hover:text-white",
              )}
              aria-expanded={megaOpen}
              aria-controls="mega-associations"
            >
              Associations
              <FaChevronDown
                size={11}
                className={cn("transition", megaOpen && "rotate-180")}
              />
            </button>

            {NAV.slice(1).map((item) => (
              <NavLink key={item.to} to={item.to} className={navLinkClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-1.5 lg:flex">
            {clubInfo.facebook && (
              <IconButton
                label="Facebook"
                onClick={() => openExternal(clubInfo.facebook)}
              >
                <FaFacebookF />
              </IconButton>
            )}

            {clubInfo.instagram && (
              <IconButton
                label="Instagram"
                onClick={() => openExternal(clubInfo.instagram)}
              >
                <FaInstagram />
              </IconButton>
            )}

            <IconButton
              label={darkMode ? "Mode clair" : "Mode sombre"}
              onClick={() => setDarkMode((value) => !value)}
            >
              {darkMode ? <FaSun /> : <FaMoon />}
            </IconButton>

            <IconButton label="Espace admin" onClick={() => go("/login")}>
              <FaCog />
            </IconButton>
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-950 shadow-sm transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-white dark:hover:bg-slate-800 lg:hidden"
            aria-label="Ouvrir le menu"
          >
            <IoIosMenu className="text-3xl" />
          </button>
        </div>
      </div>

      {megaOpen && (
        <div id="mega-associations" ref={megaRef} className="hidden lg:block">
          <div className="mx-auto max-w-7xl px-4 pb-4 lg:px-6">
            <div className="mt-2 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl shadow-slate-900/10 dark:border-white/10 dark:bg-slate-950 dark:shadow-black/40">
              <MegaSearch q={q} setQ={setQ} onSeeAll={() => go("/sections")} />

              <div className="grid grid-cols-12">
                <CategoriesColumn
                  categories={categories}
                  activeCatId={activeCatId}
                  setActiveCatId={setActiveCatId}
                  setSelectedSectionId={setSelectedSectionId}
                  allCount={allCount}
                  categoryCounts={categoryCounts}
                />

                <AssociationsColumn
                  title={associationsTitle}
                  sections={filteredSections}
                  selectedSectionId={selectedSectionId}
                  setSelectedSectionId={setSelectedSectionId}
                />

                <SelectedAssociationPanel
                  selectedSection={selectedSection}
                  go={go}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {mobileOpen && (
        <MobileDrawer
          clubInfo={clubInfo}
          nav={NAV}
          go={go}
          onClose={() => setMobileOpen(false)}
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          mobileAssociationsOpen={mobileAssociationsOpen}
          setMobileAssociationsOpen={setMobileAssociationsOpen}
          q={q}
          setQ={setQ}
          categories={categories}
          activeCatId={activeCatId}
          setActiveCatId={setActiveCatId}
          sections={filteredSections}
          allCount={allCount}
          categoryCounts={categoryCounts}
          location={location}
        />
      )}
    </header>
  );
}

function IconButton({ label, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-10 w-10 items-center justify-center rounded-2xl text-slate-600 transition hover:bg-white hover:text-slate-950 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
      title={label}
      aria-label={label}
    >
      {children}
    </button>
  );
}

function MegaSearch({ q, setQ, onSeeAll }) {
  return (
    <div className="flex items-center gap-3 border-b border-slate-200 p-4 dark:border-white/10">
      <div className="flex flex-1 items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 dark:border-white/10 dark:bg-slate-900">
        <FiSearch className="text-slate-400" />
        <input
          value={q}
          onChange={(event) => setQ(event.target.value)}
          placeholder="Rechercher une association, une discipline, un responsable..."
          className="w-full bg-transparent text-sm font-medium text-slate-950 outline-none placeholder:text-slate-400 dark:text-white"
        />
      </div>

      <button
        type="button"
        onClick={onSeeAll}
        className="rounded-2xl bg-primary px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:opacity-90"
      >
        Voir toutes
      </button>
    </div>
  );
}

function CategoriesColumn({
  categories,
  activeCatId,
  setActiveCatId,
  setSelectedSectionId,
  allCount,
  categoryCounts,
}) {
  return (
    <div className="col-span-3 border-r border-slate-200 p-3 dark:border-white/10">
      <div className="px-2 pb-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
        Catégories
      </div>

      <div className="max-h-[390px] space-y-1 overflow-auto pr-1">
        {categories.map((category) => {
          const active = Number(activeCatId) === Number(category.id);

          const count =
            Number(category.id) === 0
              ? allCount
              : categoryCounts[String(category.id)] || 0;

          return (
            <button
              key={category.id}
              type="button"
              onClick={() => {
                setActiveCatId(category.id);
                setSelectedSectionId(null);
              }}
              className={cn(
                "flex w-full items-center justify-between gap-3 rounded-2xl px-3 py-2.5 text-left text-sm font-bold transition",
                active
                  ? "bg-slate-950 text-white dark:bg-white dark:text-slate-950"
                  : "text-slate-700 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white",
              )}
            >
              <span className="truncate">{category.label || "Catégorie"}</span>
              <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs opacity-80">
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AssociationsColumn({
  title,
  sections,
  selectedSectionId,
  setSelectedSectionId,
}) {
  return (
    <div className="col-span-5 border-r border-slate-200 p-3 dark:border-white/10">
      <div className="flex items-baseline justify-between gap-4 px-2 pb-2">
        <div className="truncate text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
          {title}
        </div>

        <div className="shrink-0 text-xs font-semibold text-slate-400">
          {sections.length}{" "}
          {sections.length > 1 ? "associations" : "association"}
        </div>
      </div>

      <div className="max-h-[390px] overflow-auto pr-1">
        {sections.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 p-5 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
            Aucune association trouvée.
          </div>
        ) : (
          <div className="space-y-1">
            {sections.map((section) => {
              const active = String(section.id) === String(selectedSectionId);
              const logo = section.logo_section || section.photoURL;

              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setSelectedSectionId(section.id)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition",
                    active
                      ? "bg-primary/10 ring-1 ring-primary/20"
                      : "hover:bg-slate-50 dark:hover:bg-white/5",
                  )}
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-900">
                    {logo ? (
                      <img
                        src={toMediaUrl(logo)}
                        alt=""
                        className="h-full w-full object-contain p-1"
                      />
                    ) : (
                      <div className="h-full w-full bg-gradient-to-br from-primary via-blue-600 to-emerald-500" />
                    )}
                  </div>

                  <div className="min-w-0">
                    <div className="truncate text-sm font-bold text-slate-950 dark:text-white">
                      {section.nom_section || "Association"}
                    </div>

                    <div className="truncate text-xs font-medium text-slate-500 dark:text-slate-400">
                      {section.discipline_section ||
                        section.responsable_section ||
                        "Fiche association"}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function SelectedAssociationPanel({ selectedSection, go }) {
  if (!selectedSection) {
    return (
      <div className="col-span-4 p-5">
        <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-400">
          Sélectionnez une association pour afficher ses informations.
        </div>
      </div>
    );
  }

  const logo = selectedSection.logo_section || selectedSection.photoURL;

  return (
    <div className="col-span-4 p-5">
      <div className="flex h-full flex-col">
        <div className="flex items-center gap-3">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-3xl border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-900">
            {logo ? (
              <img
                src={toMediaUrl(logo)}
                alt=""
                className="h-full w-full object-contain p-2"
              />
            ) : (
              <div className="h-full w-full bg-gradient-to-br from-primary via-blue-600 to-emerald-500" />
            )}
          </div>

          <div className="min-w-0">
            <div className="truncate text-lg font-bold text-slate-950 dark:text-white">
              {selectedSection.nom_section}
            </div>

            <div className="truncate text-sm font-medium text-slate-500 dark:text-slate-400">
              {selectedSection.discipline_section || "Association"}
            </div>
          </div>
        </div>

        <div className="mt-5 space-y-3 text-sm">
          {selectedSection.tel_section && (
            <InfoLine icon={FiPhone} text={selectedSection.tel_section} />
          )}

          {selectedSection.mail_section && (
            <InfoLine icon={FiMail} text={selectedSection.mail_section} />
          )}

          {selectedSection.adresse_section && (
            <InfoLine icon={FiMapPin} text={selectedSection.adresse_section} />
          )}

          {selectedSection.siteweb_section && (
            <InfoLine
              icon={FiGlobe}
              text={niceUrl(selectedSection.siteweb_section)}
            />
          )}
        </div>

        <div className="mt-auto flex items-center gap-2 pt-6">
          {selectedSection.siteweb_section && (
            <button
              type="button"
              onClick={() => openExternal(selectedSection.siteweb_section)}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
            >
              <FiExternalLink />
              Site
            </button>
          )}

          <button
            type="button"
            onClick={() => go(sectionUrl(selectedSection))}
            className="ml-auto rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white transition hover:opacity-90"
          >
            Voir la fiche
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoLine({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
      <Icon className="shrink-0 text-primary" />
      <span className="truncate">{text}</span>
    </div>
  );
}

function MobileDrawer({
  clubInfo,
  nav,
  go,
  onClose,
  darkMode,
  setDarkMode,
  mobileAssociationsOpen,
  setMobileAssociationsOpen,
  q,
  setQ,
  categories,
  activeCatId,
  setActiveCatId,
  sections,
  allCount,
  categoryCounts,
  location,
}) {
  const associationsActive =
    location.pathname.startsWith("/sections") ||
    location.pathname.startsWith("/associations");

  return (
    <div className="fixed inset-0 z-[99999] lg:hidden">
      <button
        type="button"
        className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Fermer le menu"
      />

      <aside className="fixed bottom-0 right-0 top-0 flex h-[100dvh] min-h-[100dvh] w-[90%] max-w-sm flex-col overflow-hidden border-l border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-slate-950">
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-950">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden p-1 dark:border-white/10 dark:bg-slate-900">
              {clubInfo.logo ? (
                <img
                  src={toMediaUrl(clubInfo.logo)}
                  alt={clubInfo.nom}
                  className="h-full w-full object-contain"
                />
              ) : (
                <div className="h-full w-full rounded-xl bg-gradient-to-br from-primary via-blue-600 to-emerald-500" />
              )}
            </div>

            <div className="min-w-0">
              {/* <p className="truncate text-lg font-semibold leading-tight text-slate-950 dark:text-white">
                {clubInfo.nom}
              </p> */}
              <p className="truncate text-lg font-semibold leading-tight text-slate-950 dark:text-white">
                MonAppli<span className="text-blue-500 font-extrabold">Asso</span>
              </p>

              <p className="truncate text-xs font-medium text-slate-500 dark:text-slate-400">
                Menu principal
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/10"
            aria-label="Fermer"
          >
            <IoIosClose className="text-3xl" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto bg-white p-4 dark:bg-slate-950">
          <div className="space-y-2">
            <MobileNavButton
              active={location.pathname === nav[0].to}
              onClick={() => go(nav[0].to)}
            >
              {nav[0].label}
            </MobileNavButton>

            <button
              type="button"
              onClick={() => setMobileAssociationsOpen((current) => !current)}
              className={cn(
                "flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left text-sm font-bold transition",
                associationsActive || mobileAssociationsOpen
                  ? "bg-primary text-white"
                  : "text-slate-800 hover:bg-slate-100 dark:text-white dark:hover:bg-white/10",
              )}
            >
              <span className="inline-flex items-center gap-2">
                <FaUsers />
                Associations
              </span>

              <FaChevronDown
                size={12}
                className={cn(
                  "transition",
                  mobileAssociationsOpen && "rotate-180",
                )}
              />
            </button>

            {mobileAssociationsOpen && (
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-slate-900/60">
                <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-slate-950">
                  <FiSearch className="text-slate-400" />

                  <input
                    value={q}
                    onChange={(event) => setQ(event.target.value)}
                    placeholder="Rechercher..."
                    className="w-full bg-transparent text-sm text-slate-950 outline-none placeholder:text-slate-400 dark:text-white"
                  />
                </div>

                <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                  {categories.map((category) => {
                    const active = Number(activeCatId) === Number(category.id);

                    const count =
                      Number(category.id) === 0
                        ? allCount
                        : categoryCounts[String(category.id)] || 0;

                    return (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => setActiveCatId(category.id)}
                        className={cn(
                          "shrink-0 rounded-full px-3 py-1.5 text-xs font-bold transition",
                          active
                            ? "bg-primary text-white"
                            : "bg-white text-slate-600 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-300 dark:ring-white/10",
                        )}
                      >
                        {category.label} · {count}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-3 max-h-72 space-y-1 overflow-y-auto">
                  {sections.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-200 p-4 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                      Aucune association trouvée.
                    </div>
                  ) : (
                    sections.map((section) => {
                      const logo = section.logo_section || section.photoURL;

                      return (
                        <button
                          key={section.id}
                          type="button"
                          onClick={() => go(sectionUrl(section))}
                          className="flex w-full items-center gap-3 rounded-2xl px-3 py-2 text-left transition hover:bg-white dark:hover:bg-white/10"
                        >
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950">
                            {logo ? (
                              <img
                                src={toMediaUrl(logo)}
                                alt=""
                                className="h-full w-full object-contain p-1"
                              />
                            ) : (
                              <div className="h-full w-full bg-gradient-to-br from-primary via-blue-600 to-emerald-500" />
                            )}
                          </div>

                          <div className="min-w-0">
                            <p className="truncate text-sm font-bold text-slate-950 dark:text-white">
                              {section.nom_section}
                            </p>

                            <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                              {section.discipline_section ||
                                section.responsable_section ||
                                "Fiche association"}
                            </p>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => go("/sections")}
                  className="mt-3 w-full rounded-2xl bg-primary px-4 py-2.5 text-sm font-bold text-white"
                >
                  Voir toutes les associations
                </button>
              </div>
            )}

            {nav.slice(1).map((item) => (
              <MobileNavButton
                key={item.to}
                active={location.pathname === item.to}
                onClick={() => go(item.to)}
              >
                {item.label}
              </MobileNavButton>
            ))}
          </div>
        </div>

        <div className="shrink-0 border-t border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-950">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setDarkMode((value) => !value)}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-white/10 dark:text-slate-200 dark:hover:bg-white/15"
            >
              {darkMode ? <FaSun /> : <FaMoon />}
              {darkMode ? "Mode clair" : "Mode sombre"}
            </button>

            <button
              type="button"
              onClick={() => go("/login")}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-white/10 dark:text-slate-200 dark:hover:bg-white/15"
              title="Admin"
              aria-label="Espace admin"
            >
              <FaCog />
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function MobileNavButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full rounded-2xl px-4 py-3 text-left text-sm font-bold transition",
        active
          ? "bg-primary text-white"
          : "text-slate-800 hover:bg-slate-100 dark:text-white dark:hover:bg-white/10",
      )}
    >
      {children}
    </button>
  );
}